import { useState, useRef } from "react";
import api from "../lib/api";
import { Upload, X, Image as ImageIcon, Film, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * File upload widget. Calls /api/admin/upload, returns the full public URL.
 * Use for posters, banners, channel logos, video files.
 *
 * Props:
 *   value:  current URL (string)
 *   onChange(url):  called with new URL after upload
 *   accept:  "image/*" | "video/*" | undefined
 *   label:   field label
 *   kind:    "image" | "video" — for preview rendering
 */
export default function FileUpload({ value, onChange, accept = "image/*", label, kind = "image" }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const isUploaded = value && value.startsWith("/api/files/");
  const displayUrl = isUploaded ? `${BACKEND_URL}${value}` : value;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      onChange(data.url);
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange("");
  const Icon = kind === "video" ? Film : ImageIcon;

  return (
    <div data-testid={`upload-${label?.toLowerCase().replace(/\s+/g, "-")}`}>
      {label && <label className="text-xs uppercase tracking-wider text-[#888] block mb-1.5">{label}</label>}
      <div className="flex gap-3 items-start">
        <div
          onClick={() => inputRef.current?.click()}
          className="flex-shrink-0 w-24 h-24 rounded-md border border-dashed border-white/15 bg-[#0a0a0a] hover:border-[#E50914] hover:bg-[#141414] transition cursor-pointer flex items-center justify-center overflow-hidden relative"
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-1">
              <Loader2 className="animate-spin text-[#E50914]" size={20} />
              <span className="text-[10px] text-[#888]">{progress}%</span>
            </div>
          ) : displayUrl && kind === "image" ? (
            <img src={displayUrl} alt="" className="w-full h-full object-cover" />
          ) : displayUrl && kind === "video" ? (
            <Film className="text-[#E50914]" size={28} />
          ) : (
            <Icon className="text-[#666]" size={28} />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <div className="flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="btn-secondary !py-1.5 !px-3 !text-xs">
              <Upload size={14} /> {uploading ? "Uploading..." : "Upload"}
            </button>
            {value && (
              <button type="button" onClick={clear} className="btn-secondary !py-1.5 !px-3 !text-xs text-[#E50914]">
                <X size={14} /> Clear
              </button>
            )}
          </div>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`or paste ${kind} URL...`}
            className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/10 rounded-md text-xs focus:outline-none focus:border-[#E50914]"
          />
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}
